import React, { useMemo, useEffect, useCallback } from 'react';
import { useQuery } from 'react-query';
import {
  Form,
  Popconfirm,
  notification,
  Row,
  Col,
  Input,
  InputNumber,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';

import { indexBy, setValueByNamePath } from 'utils/utils2';
import DataGrid from 'components/DataGrid/DataGrid';
import ParListSelect from 'components/ParListSelect';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import { useFormList } from 'hooks/useFormList';
import DeleteButton from 'components/DeleteButton';
import ButtonOperation from 'components/ButtonOperation';
import ApplicationRule from 'pages/Configuration/RuleEngine/ApplicationRule';
import SaveButtonIcon from 'components/SaveBtn';
import {
  addParAssetFvContextCriteria,
  getParAssetFvContextCriteriaList,
  ParAssetFvContextCriteria,
  ParAssetFvContextCriteriaResponse,
  removeParAssetFvContextCriteria,
} from 'services/assetconfig';

const NEW_FUTURE_CRITERIA_ID = -1;

type ParAssetFutureCriteriaTableProps = {
  parcontextid: number;
  isDisplay?: boolean;
  heideButtons?: boolean;
};

const ParAssetFutureCriteriaTable = (
  props: ParAssetFutureCriteriaTableProps
) => {
  const { parcontextid, heideButtons } = props;
  const [form] = Form.useForm();
  const fieldName = 'criteria';

  const { add, remove } = useFormList(form, fieldName, {
    idKey: 'criid',
    addAsFirst: true,
  });

  const { data, refetch, isFetching } = useQuery(
    ['getParAssetFvContextCriteriaList', parcontextid],
    () =>
      getParAssetFvContextCriteriaList({ contextid: parcontextid }).then(
        (res) => res.list || []
      ),
    { refetchOnMount: false }
  );

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        [fieldName]: data,
      });
    } else {
      form.setFieldsValue({
        [fieldName]: [],
      });
    }
  }, [form, data]);

  const addRow = () => {
    const row: ParAssetFvContextCriteriaResponse = {
      criid: NEW_FUTURE_CRITERIA_ID,
      parcontextid: parcontextid,
      critcode: '',
      critcodelabel: '',
      critvalue: '',
      critvaluelabel: '',
      critvaluemax: '',
      critoperand: '',
      listvalue: '',
      applicationrule: '',
    };
    add(row);
  };

  const onSave = useCallback(
    (record: ParAssetFvContextCriteriaResponse) => {
      const steps: ParAssetFvContextCriteriaResponse[] =
        form.getFieldValue(fieldName);
      const index = indexBy(steps, 'criid')[record.criid];
      const currentValue = steps[index];
      addParAssetFvContextCriteria({
        request: { ...currentValue },
        contextid: parcontextid,
      }).then((response) => {
        if (response.code === '200') {
          notification.success({
            message: response.message,
          });
          refetch();
        }
      });
    },
    [form, refetch]
  );

  const columns = useMemo<DataGridColDef<any>[]>(
    () => [
      {
        dataIndex: 'criid',
        width: 50,
        render: (_value, _record, index) => _value,
      },
      {
        dataIndex: 'critcode',
        render: (_value, _record, index) => (
          <Form.Item
            name={[
              fieldName,
              indexBy(form.getFieldValue(fieldName), 'criid')[_record.criid],
              'critcode',
            ]}
            noStyle
          >
            <ParListSelect
              parcode="ENTATTRIB"
              onChange={(e) => {
                setValueByNamePath(
                  form,
                  [
                    fieldName,
                    indexBy(form.getFieldValue(fieldName), 'criid')[
                      _record.criid
                    ],
                    'critoperand',
                  ],
                  ''
                );
                setValueByNamePath(
                  form,
                  [
                    fieldName,
                    indexBy(form.getFieldValue(fieldName), 'criid')[
                      _record.criid
                    ],
                    'critvalue',
                  ],
                  e === 'RULE' ? 'RULE' : ''
                );
              }}
            />
          </Form.Item>
        ),
        width: 180,
      },
      {
        dataIndex: 'critoperand',
        render: (_value, _record, index) => (
          <Form.Item
            name={[
              fieldName,
              indexBy(form.getFieldValue(fieldName), 'criid')[_record.criid],
              'critoperand',
            ]}
            noStyle
          >
            <ParListSelect
              parcode="CROPERAND"
              disabled={_record.critcode === 'RULE'}
            />
          </Form.Item>
        ),
        width: 180,
      },
      {
        dataIndex: 'critvalue',
        render: (_value, _record, index) => (
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => (
              <Form.Item
                name={[
                  fieldName,
                  indexBy(getFieldValue(fieldName), 'criid')[_record.criid],
                  'critvalue',
                ]}
                noStyle
              >
                {getFieldValue([fieldName, index, 'critoperand']) ===
                'VALUE' ? (
                  <ParListSelect
                    parcode={getFieldValue([fieldName, index, 'critcode'])}
                    disabled={_record.critcode === 'RULE'}
                  />
                ) : getFieldValue([fieldName, index, 'critoperand']) ===
                  'LIST' ? null : (
                  <Input disabled={_record.critcode === 'RULE'} />
                )}
              </Form.Item>
            )}
          </Form.Item>
        ),
        width: 160,
      },
      {
        dataIndex: 'critvaluemax',
        render: (_value, _record, index) => (
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => (
              <Form.Item
                name={[
                  fieldName,
                  indexBy(getFieldValue(fieldName), 'criid')[_record.criid],
                  'critvaluemax',
                ]}
                noStyle
              >
                <InputNumber
                  disabled={_record.critoperand !== 'BETWEEN'}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            )}
          </Form.Item>
        ),
        width: 160,
      },
      {
        dataIndex: 'listvalue',
        render: (_value, _record, index) => (
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => (
              <Form.Item
                name={[
                  fieldName,
                  indexBy(getFieldValue(fieldName), 'criid')[_record.criid],
                  'listvalue',
                ]}
                noStyle
              >
                <Input
                  disabled={
                    getFieldValue([fieldName, index, 'critoperand']) !== 'LIST'
                  }
                />
              </Form.Item>
            )}
          </Form.Item>
        ),
        width: 160,
      },
      {
        dataIndex: 'applicationrule',
        render: (_value, _record, index) => (
          <div>
            <ApplicationRule
              noStyle
              form={form}
              module="PROPOSAL"
              name={[
                fieldName,
                indexBy(form.getFieldValue(fieldName), 'criid')[_record.criid],
                'applicationrule',
              ]}
              disabled={_record.critcode !== 'RULE'}
            />
          </div>
        ),
        width: 160,
      },
      {
        key: 'actions',
        render: (_, _record) =>
          !heideButtons && (
            <Row gutter={10}>
              <Col xs={12} lg={12} xl={12}>
                {_record.criid === NEW_FUTURE_CRITERIA_ID && (
                  <SaveButtonIcon
                    onClick={(ev) => {
                      ev.preventDefault();
                      onSave(_record);
                    }}
                    size="small"
                    onlyIcon
                  />
                )}
              </Col>
              <Col xs={6} lg={6} xl={6}>
                {_record.criid === NEW_FUTURE_CRITERIA_ID ? (
                  <DeleteButton
                    onlyIcon
                    onClick={() => remove(NEW_FUTURE_CRITERIA_ID)}
                  />
                ) : (
                  <Popconfirm
                    title="Sure to delete?"
                    onConfirm={() => {
                      removeParAssetFvContextCriteria({
                        criid: _record.criid,
                        contextid: _record.parcontextid,
                      }).then((res) => {
                        notification.success({ message: res.message });
                        refetch();
                      });
                    }}
                  >
                    <DeleteButton onlyIcon />
                  </Popconfirm>
                )}
              </Col>
            </Row>
          ),
        width: 120,
        fixed: 'right',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form, onSave, refetch, remove]
  );

  return (
    <Form form={form} initialValues={INITIAL_VALUES}>
      <Form.Item noStyle shouldUpdate>
        {({ getFieldValue }) => {
          const criteria: ParAssetFvContextCriteria[] =
            getFieldValue(fieldName);

          const isNewItemInTable =
            criteria?.find(({ criid }) => criid === NEW_FUTURE_CRITERIA_ID) !==
            undefined;

          return (
            <DataGrid
              rowKey="criid"
              columns={columns}
              dataSource={criteria}
              isLoading={isFetching}
              titleComcode="futureContextCriteria"
              hideFilters
              extraActions={
                !heideButtons && (
                  <ButtonOperation
                    type="primary"
                    icon={<PlusOutlined />}
                    disabled={isNewItemInTable}
                    onClick={addRow}
                    comcode="add"
                  />
                )
              }
            />
          );
        }}
      </Form.Item>
    </Form>
  );
};

const INITIAL_VALUES = {
  criteria: [],
};

export default ParAssetFutureCriteriaTable;
